import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  BellIcon,
  MegaphoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { UserContext } from '../App';
import { db } from '../firebase';
import {
  formatAnnouncementDate,
  getUnreadAnnouncements,
  isSafeCtaPath,
  sortAnnouncements,
  timestampToMillis,
} from '../utils/announcements';

const LOCAL_PREVIEW_ANNOUNCEMENT = {
  id: 'local-announcement-preview',
  title: 'Bandz Journal was updated',
  summary: 'A quick look at the latest improvements.',
  bullets: [
    'The new journal keeps P&L, screenshots, and trade context together.',
    'Analytics and AI Context now understand the new journal fields.',
    'Account Activity keeps deposits and payouts in one place.',
  ],
  ctaLabel: 'Open the new journal',
  ctaPath: '/quick-add',
  status: 'published',
  publishedAt: '2026-08-10T12:00:00.000Z',
};

function isLocalPreviewEnabled() {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('announcementPreview') === '1';
}

const AnnouncementCenter = ({
  announcementsOverride = null,
  readStateOverride = null,
  onMarkReadOverride = null,
}) => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [readState, setReadState] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loadError, setLoadError] = useState('');

  const usingOverride = Array.isArray(announcementsOverride);

  useEffect(() => {
    if (usingOverride) {
      setAnnouncements(sortAnnouncements(announcementsOverride));
      setReadState(readStateOverride || {});
      setLoaded(true);
      return undefined;
    }

    if (isLocalPreviewEnabled()) {
      setAnnouncements([LOCAL_PREVIEW_ANNOUNCEMENT]);
      setReadState({});
      setLoaded(true);
      return undefined;
    }

    if (!currentUser?.uid) {
      setAnnouncements([]);
      setReadState({});
      setLoaded(true);
      return undefined;
    }

    let cancelled = false;
    setLoaded(false);
    setLoadError('');

    const readStateRef = doc(
      db,
      'users',
      currentUser.uid,
      'preferences',
      'announcements'
    );

    getDoc(readStateRef)
      .then((snapshot) => {
        if (!cancelled) setReadState(snapshot.exists() ? snapshot.data() : {});
      })
      .catch((error) => {
        console.error('Unable to load announcement read state:', error);
        if (!cancelled) setReadState({});
      });

    const publishedQuery = query(
      collection(db, 'announcements'),
      where('status', '==', 'published')
    );

    const unsubscribe = onSnapshot(
      publishedQuery,
      (snapshot) => {
        if (cancelled) return;
        const records = snapshot.docs.map((announcementDoc) => ({
          id: announcementDoc.id,
          ...announcementDoc.data(),
        }));
        setAnnouncements(sortAnnouncements(records));
        setLoaded(true);
      },
      (error) => {
        console.error('Unable to load announcements:', error);
        if (!cancelled) {
          setAnnouncements([]);
          setLoadError('Announcements are temporarily unavailable.');
          setLoaded(true);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [announcementsOverride, currentUser?.uid, readStateOverride, usingOverride]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const orderedAnnouncements = useMemo(
    () => sortAnnouncements(announcements),
    [announcements]
  );
  const unreadAnnouncements = useMemo(
    () => getUnreadAnnouncements(orderedAnnouncements, readState),
    [orderedAnnouncements, readState]
  );

  const openAnnouncements = async () => {
    const newest = orderedAnnouncements[0];
    if (!newest) return;

    const nextReadState = {
      lastSeenAnnouncementId: newest.id,
      lastSeenPublishedAt: new Date(
        timestampToMillis(newest.publishedAt || newest.createdAt) || Date.now()
      ).toISOString(),
    };

    setReadState(nextReadState);
    setIsOpen(true);

    if (onMarkReadOverride) {
      onMarkReadOverride(nextReadState);
      return;
    }

    if (usingOverride || isLocalPreviewEnabled() || !currentUser?.uid) return;

    try {
      const readStateRef = doc(
        db,
        'users',
        currentUser.uid,
        'preferences',
        'announcements'
      );
      await setDoc(readStateRef, {
        ...nextReadState,
        lastOpenedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.error('Unable to save announcement read state:', error);
    }
  };

  const handleCta = (announcement) => {
    // Announcement buttons may only open an in-app path or an https URL (never javascript:, data:, etc.).
    if (!isSafeCtaPath(announcement.ctaPath)) return;
    setIsOpen(false);
    if (announcement.ctaPath.startsWith('/')) {
      navigate(announcement.ctaPath);
      return;
    }
    window.location.assign(announcement.ctaPath);
  };

  if (!loaded && !isOpen) return null;

  return (
    <>
      {unreadAnnouncements.length > 0 && !isOpen && (
        <button
          type="button"
          onClick={openAnnouncements}
          className="theme-input theme-hover-surface relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border shadow-sm transition-colors"
          aria-label={`${unreadAnnouncements.length} new announcement${unreadAnnouncements.length === 1 ? '' : 's'}`}
          title="New announcement"
        >
          <BellIcon className="theme-text-muted h-3.5 w-3.5" />
          <span className="absolute -right-1 -top-1 flex min-h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[var(--theme-accent)] px-1 text-[8px] font-bold leading-none text-white">
            {Math.min(unreadAnnouncements.length, 9)}
          </span>
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-[120]">
          <button
            type="button"
            className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]"
            onClick={() => setIsOpen(false)}
            aria-label="Close announcements"
          />
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="announcement-panel-title"
            className="theme-app-shell theme-border absolute bottom-0 right-0 top-0 flex w-full flex-col border-l shadow-2xl sm:w-[66vw] sm:max-w-[760px]"
          >
            <header className="theme-panel theme-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
              <div className="flex min-w-0 items-center gap-3">
                <span className="theme-accent-soft flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl">
                  <MegaphoneIcon className="theme-accent h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h2 id="announcement-panel-title" className="theme-text text-lg font-bold">
                    What's new
                  </h2>
                  <p className="theme-text-muted text-xs">Updates from Bandz Journal</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="theme-hover-surface theme-text-muted flex h-8 w-8 items-center justify-center rounded-lg"
                aria-label="Close announcement panel"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </header>

            <div className="themed-scrollbar flex-1 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
              {loadError && (
                <div className="theme-panel theme-border theme-text-muted mb-4 rounded-xl border px-4 py-3 text-sm">
                  {loadError}
                </div>
              )}

              <div className="space-y-4">
                {orderedAnnouncements.map((announcement, index) => (
                  <article
                    key={announcement.id}
                    className="theme-panel theme-border rounded-2xl border p-5 shadow-subtle sm:p-6"
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      {index === 0 ? (
                        <span className="theme-accent-chip rounded-full px-2 py-1 text-[9px] font-bold uppercase tracking-[0.16em]">
                          Latest
                        </span>
                      ) : <span />}
                      <time className="theme-text-muted text-[11px] font-medium">
                        {formatAnnouncementDate(announcement.publishedAt || announcement.createdAt)}
                      </time>
                    </div>

                    <h3 className="theme-text text-xl font-bold tracking-tight">
                      {announcement.title}
                    </h3>
                    {announcement.summary && (
                      <p className="theme-text-muted mt-2 text-sm leading-6">
                        {announcement.summary}
                      </p>
                    )}

                    {Array.isArray(announcement.bullets) && announcement.bullets.length > 0 && (
                      <ul className="mt-4 space-y-2.5">
                        {announcement.bullets.map((bullet, bulletIndex) => (
                          <li key={`${announcement.id}-${bulletIndex}`} className="theme-text flex gap-3 text-sm leading-6">
                            <span className="mt-2.5 h-1 w-1 flex-shrink-0 rounded-full bg-[var(--theme-accent)]" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {announcement.ctaLabel && isSafeCtaPath(announcement.ctaPath) && (
                      <button
                        type="button"
                        onClick={() => handleCta(announcement)}
                        className="theme-btn-secondary mt-5 inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-bold"
                      >
                        {announcement.ctaLabel}
                        <ArrowRightIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default AnnouncementCenter;
